import { useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Filter, Loader2, RefreshCw, X, Search } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useStateMachine } from "@/core/arch/state_machine";
import { EventsRepository } from "@/application/events/events_repository";
import { EventsListBloc } from "@/application/events/events_list_bloc";
import type { ScopedModeOptions } from "@/application/events/events_list_bloc";
import type {
  UnifiedEvent,
  EventFilters,
  EventSource,
} from "@/application/events/types";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/base/select";
import { Badge } from "@/components/ui/base/badge";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { EventTypeBadge } from "./components/EventTypeBadge";
import { EventProcessingBadge } from "./components/EventProcessingBadge";
import { AccountLink } from "./components/AccountLink";
import { ProviderPill } from "@/components/ui/ProviderPill";
import { toPillProvider } from "@/components/ui/provider-pill-providers";
import type { ProcessingStatus } from "@/application/events/types";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelative(unix: number): string {
  const now = Date.now();
  const then = unix * 1000;
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatAbsolute(unix: number): string {
  return new Date(unix * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ── Date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS: {
  label: string;
  value: EventFilters["dateRange"]["preset"];
}[] = [
  { label: "Last 1 hour", value: "1h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
];

const STATUS_OPTIONS: { value: ProcessingStatus | "unknown"; label: string }[] =
  [
    { value: "success", label: "Success" },
    { value: "failed", label: "Failed" },
    { value: "pending", label: "Pending" },
    { value: "unhandled", label: "Unhandled" },
    // Store rows only, and both terminal. Without these the rows render as
    // "Unknown" and cannot be filtered for at all.
    { value: "skipped", label: "Skipped" },
    { value: "parse_error", label: "Parse error" },
    { value: "rejected_binding", label: "Rejected binding" },
    { value: "unknown", label: "Unknown" },
  ];

function dateRangeLabel(
  preset: EventFilters["dateRange"]["preset"],
): string | null {
  if (preset === "all" || preset === "custom") return null;
  const match = DATE_PRESETS.find((p) => p.value === preset);
  return match?.label ?? null;
}

// ── Filter toolbar ────────────────────────────────────────────────────────────

interface FilterToolbarProps {
  filters: EventFilters;
  source: EventSource;
  onFilterChange: (patch: Partial<EventFilters>) => void;
  onReset: () => void;
  isScoped: boolean;
}

function FilterToolbar({
  filters,
  source,
  onFilterChange,
  onReset,
  isScoped,
}: FilterToolbarProps) {
  // Non-date active filters contribute to the numeric count badge.
  const nonDateActiveCount =
    (filters.typeQuery ? 1 : 0) +
    (filters.livemode !== "all" ? 1 : 0) +
    (filters.account ? 1 : 0) +
    (filters.statusFilter.length > 0 ? 1 : 0);

  const activeDateLabel = dateRangeLabel(filters.dateRange.preset);
  const isFiltered = nonDateActiveCount > 0 || activeDateLabel !== null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Type search */}
      <div className="relative">
        <Search className="text-subtle-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Filter by type..."
          className="w-52 pl-9"
          value={filters.typeQuery}
          onChange={(e) => onFilterChange({ typeQuery: e.target.value })}
          aria-label="Filter by event type"
        />
      </div>

      {/* Filters popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeDateLabel && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1.5 text-[10px] font-normal"
              >
                {activeDateLabel}
              </Badge>
            )}
            {nonDateActiveCount > 0 && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1 text-[10px] font-normal"
              >
                +{nonDateActiveCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="flex flex-col gap-3">
            {/* Date range */}
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                Date range
              </span>
              <Select
                value={filters.dateRange.preset}
                onValueChange={(v) => {
                  const preset = v as Exclude<
                    EventFilters["dateRange"]["preset"],
                    "custom"
                  >;
                  onFilterChange({ dateRange: { preset } });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Livemode */}
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                Livemode
              </span>
              <Select
                value={filters.livemode}
                onValueChange={(v) =>
                  onFilterChange({
                    livemode: v as EventFilters["livemode"],
                  })
                }
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Live</SelectItem>
                  <SelectItem value="false">Test</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Processing status */}
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                Status
              </span>
              <div className="flex flex-wrap gap-1">
                {STATUS_OPTIONS.map((opt) => {
                  const isSelected = filters.statusFilter.includes(
                    opt.value as ProcessingStatus,
                  );
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        const next = isSelected
                          ? filters.statusFilter.filter((s) => s !== opt.value)
                          : [
                              ...filters.statusFilter,
                              opt.value as ProcessingStatus,
                            ];
                        onFilterChange({ statusFilter: next });
                      }}
                      className={[
                        "rounded-md border px-2 py-0.5 text-xs transition-colors",
                        isSelected
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-border text-muted-foreground hover:border-muted-foreground/40",
                      ].join(" ")}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Account (connected tab only, not in scoped mode) */}
            {source === "connected" && !isScoped && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs font-medium">
                  Account
                </span>
                <Input
                  placeholder="acct_..."
                  className="h-8 text-sm"
                  value={filters.account ?? ""}
                  onChange={(e) =>
                    onFilterChange({
                      account: e.target.value || null,
                    })
                  }
                  aria-label="Filter by connected account ID"
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {isFiltered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onReset}
          aria-label="Reset filters"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

interface EventsViewProps {
  /**
   * When provided, locks the source and account — used for merchant-detail embed.
   */
  scopedMode?: ScopedModeOptions;
}

export function EventsView({ scopedMode }: EventsViewProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [state, bloc] = useStateMachine(
    () =>
      new EventsListBloc(new EventsRepository(), {
        scopedMode,
      }),
  );

  // Source is controlled by URL param when not scoped. Coerce unknown values to
  // "platform" so a malformed/tampered URL can't push the bloc into an invalid
  // state.
  const rawSource = searchParams.get("source");
  const urlSource: EventSource =
    rawSource === "connected" || rawSource === "store" ? rawSource : "platform";
  const activeSource = scopedMode ? scopedMode.source : urlSource;

  // Sync bloc source with URL (non-scoped only) and trigger initial load.
  // When `from` and `to` search params are present (unix seconds, set by the
  // Related Events cross-link from Transaction Detail), apply them as a custom
  // date-range filter before loading. When absent, the default preset applies.
  useEffect(() => {
    if (!scopedMode && bloc.state.source !== activeSource) {
      bloc.setSource(activeSource);
    }
    const rawFrom = searchParams.get("from");
    const rawTo = searchParams.get("to");
    const fromSec = rawFrom != null ? parseInt(rawFrom, 10) : NaN;
    const toSec = rawTo != null ? parseInt(rawTo, 10) : NaN;
    if (!isNaN(fromSec) && !isNaN(toSec) && fromSec > 0 && toSec > 0) {
      // Patch filter state directly (synchronous setState) so the subsequent
      // load() picks up the custom range without triggering a duplicate fetch.
      bloc.setState(
        {
          ...bloc.state,
          filters: {
            ...bloc.state.filters,
            dateRange: {
              preset: "custom",
              range: { from: fromSec, to: toSec },
            },
          },
        },
        false,
      );
    }
    void bloc.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSourceChange = useCallback(
    (src: EventSource) => {
      if (scopedMode) return;
      setSearchParams({ source: src }, { replace: true });
      bloc.setSource(src);
      void bloc.load();
    },
    [bloc, scopedMode, setSearchParams],
  );

  const handleFilterChange = useCallback(
    (patch: Partial<EventFilters>) => {
      void bloc.setFilter(patch);
    },
    [bloc],
  );

  const handleReset = useCallback(() => {
    void bloc.setFilter({
      typeQuery: "",
      dateRange: { preset: "24h" },
      livemode: "all",
      account: scopedMode?.account ?? null,
      statusFilter: [],
    });
  }, [bloc, scopedMode]);

  const showAccount = state.source === "connected";
  const showStore = state.source === "store";

  // ── Columns ────────────────────────────────────────────────────────────────

  const columns: ColumnDef<UnifiedEvent, unknown>[] = useMemo(() => {
    const cols: ColumnDef<UnifiedEvent, unknown>[] = [
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => {
          const created = row.getValue("created") as number;
          return (
            <span
              className="text-muted-foreground text-sm whitespace-nowrap"
              title={formatAbsolute(created)}
            >
              {formatRelative(created)}
            </span>
          );
        },
      },
      ...(showStore
        ? [
            {
              accessorKey: "provider",
              header: "Store",
              cell: ({ row }) => {
                const raw = row.getValue("provider") as string | null;
                const provider = toPillProvider(raw);
                if (provider) return <ProviderPill provider={provider} />;
                return (
                  <span className="text-muted-foreground text-xs capitalize">
                    {raw ?? "—"}
                  </span>
                );
              },
            } as ColumnDef<UnifiedEvent, unknown>,
          ]
        : []),
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <EventTypeBadge type={row.getValue("type") as string} />
        ),
      },
      {
        accessorKey: "processingStatus",
        header: "Status",
        cell: ({ row }) => (
          <EventProcessingBadge status={row.original.processingStatus} />
        ),
      },
      {
        accessorKey: "id",
        header: "Event ID",
        cell: ({ row }) => {
          const id = row.getValue("id") as string;
          return (
            <span className="text-muted-foreground font-mono text-xs">
              {id.slice(0, 22)}…
            </span>
          );
        },
      },
    ];

    if (showAccount) {
      cols.push({
        accessorKey: "account",
        header: "Account",
        cell: ({ row }) => (
          <AccountLink account={row.getValue("account") as string | null} />
        ),
      });
    }

    if (showStore) {
      cols.push({
        accessorKey: "environment",
        header: "Environment",
        cell: ({ row }) => {
          const environment = row.getValue("environment") as string | null;
          // `unknown` is what Google's rows legitimately hold — an RTDN carries
          // no environment marker — so it is shown as itself rather than as a
          // gap, which would read as data we failed to record.
          return (
            <span className="text-muted-foreground text-xs">
              {environment ?? "—"}
            </span>
          );
        },
      });
    }

    cols.push({
      accessorKey: "livemode",
      header: "Mode",
      cell: ({ row }) => {
        const livemode = row.getValue("livemode") as boolean | null;
        if (livemode === null) {
          return <span className="text-subtle-foreground text-xs">—</span>;
        }
        return (
          <span className="flex items-center gap-1.5">
            <span
              className={`h-2 w-2 rounded-full ${
                livemode ? "bg-green-500" : "bg-yellow-400"
              }`}
            />
            <span className="text-muted-foreground text-xs">
              {livemode ? "Live" : "Test"}
            </span>
          </span>
        );
      },
    });

    return cols;
  }, [showAccount, showStore]);

  // ── Render ────────────────────────────────────────────────────────────────

  const renderToolbar = () => (
    <FilterToolbar
      filters={state.filters}
      source={state.source}
      onFilterChange={handleFilterChange}
      onReset={handleReset}
      isScoped={!!scopedMode}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Source tabs (only shown when not scoped) */}
      {!scopedMode && (
        <div className="flex border-b">
          {(["platform", "connected", "store"] as const).map((src) => (
            <button
              key={src}
              onClick={() => handleSourceChange(src)}
              className={[
                "px-5 py-2.5 text-sm font-medium capitalize transition-colors",
                activeSource === src
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {src === "platform"
                ? "Platform"
                : src === "connected"
                  ? "Connected"
                  : "Store"}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {state.status === "loading" && (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      )}

      {/* Error */}
      {state.status === "error" && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700">
            Failed to load events: {state.error}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void bloc.load()}
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}

      {/* DataTable (handles its own pagination) */}
      {state.status === "loaded" && (
        <>
          <DataTable
            columns={columns}
            data={state.items}
            initialSorting={[{ id: "created", desc: true }]}
            onRowClick={(row) =>
              navigate(
                ROUTES.EVENT_DETAIL.replace(":source", row.source).replace(
                  ":id",
                  // A StoreNotification id is `apple#{uuid}` / `google#{id}`,
                  // and an unescaped `#` opens a URL fragment — the route saw
                  // only the provider and every store row 404'd.
                  encodeURIComponent(row.id),
                ),
              )
            }
            toolbar={renderToolbar}
          />
        </>
      )}
    </div>
  );
}
